/* eslint-disable no-shadow */
import { Dayjs } from "dayjs";

import { ExpertType, ConsultationType, Language } from "./global";

export interface ExpertIdentifierMap {
  [expertUid: string]: ExpertIdentifier;
}

export interface ExpertDataMap {
  [expertUid: string]: Omit<Expert, "presenceStatus" | "queue" | "planning">;
}

export interface ExpertStatusMap {
  [expertUid: string]: Pick<Expert, "presenceStatus" | "queue" | "planning">;
}

export interface ExpertIdentifier {
  expertUid: string;
  pseudo: string;
  expertCode: string;
  picture: string;
  online: boolean;
  hidden: boolean;
  status: string;
}

export type ExpertAvailabilityChannel = "PHONE" | "VIDEO" | "LIVECHAT" | "WRITTEN";

export interface Expert {
  expertUid: string;
  expertCode: string
  picture: string;
  pseudo: string;
  spokenLanguages: string[];
  vocalMessage: string;
  quote: string;
  presenceStatus: ExpertStatus;
  parameters: {
    phoneEnabled: boolean;
    videoEnabled: boolean;
    livechatEnabled: boolean;
    writtenEnabled: boolean;
    writtenMode: boolean;
    writtenLimit: number;
  }
  skills: Skill[];
  totalConsultations: number;
  totalRatings: number
  queue: ExpertQueue;
  planning: ExpertPlanningWeek;
}

export interface ExpertStatus {
  phoneStatus?: boolean;
  videoStatus?: boolean;
  livechatStatus?: boolean;
  writtenStatus?: boolean;
  activityStatus?: boolean;
}
export interface ExpertContent {
  description: string;
  writtenMessage: string;
}

export type PlanningAvailabilityChannel = "PHONE" | "VIDEO" | "LIVECHAT";
export type PlanningQueueChannel = "PHONE" | "VIDEO" | "LIVECHAT";

export interface PlanningSlot {
  start: Dayjs; end: Dayjs; channels: PlanningAvailabilityChannel[]
}

export interface PlanningQueueSlot {
  start: Dayjs; end: Dayjs; channels: PlanningQueueChannel[]
}

export interface ExpertPlanningWeek {
  ownerUid: string;
  from?: Dayjs;
  to?: Dayjs;
  slots: PlanningSlot[]
  queueSlots: PlanningQueueSlot[]
}

export interface ExpertQueue {
  queueUid: string;
  items: ExpertQueueItem[];
}

export interface ExpertQueueItem {
  position: number;
  duration: number;
  startedAt: string | null;
  type: "PHONE" | "VIDEO" | "LIVECHAT" | "BREAK";
  metadata?: {
    isEphemeral?: boolean;
    maxDuration?: number;
  }
}

export interface ExpertScheduleAvailability {
  from: string;
  to: string;
  channels: PlanningAvailabilityChannel[];
}

export interface ExpertScheduleDay {
  day: "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "satursday" | "sunday";
  date: string;
  available: ExpertScheduleAvailability[];
}

export enum Status {
  OFFLINE = "OFFLINE",
  BREAK = "BREAK",
  BUSY = "BUSY",
  ONLINE = "ONLINE",
}

export enum Skill {
  ASTROLOGY = "ASTROLOGY",
  CARTOMANCY = "CARTOMANCY",
  MEDIUM = "MEDIUM",
  NUMEROLOGY = "NUMEROLOGY",
  TAROLOGY = "TAROLOGY",
  CLAIRVOYANCE = "CLAIRVOYANCE",
  MAGNETISM = "MAGNETISM",
  DREAMS_INTERPRETATION = "DREAMS_INTERPRETATION"
}

export const skillsList: Skill[] = [Skill.ASTROLOGY, Skill.CARTOMANCY, Skill.MEDIUM, Skill.NUMEROLOGY, Skill.TAROLOGY, Skill.CLAIRVOYANCE, Skill.MAGNETISM, Skill.DREAMS_INTERPRETATION];

export enum ExpertOrderBy {
  DEFAULT = "DEFAULT",
  CONSULTATION = "CONSULTATION",
  EVALUATION = "EVALUATION",
  PHONE_CONSULTATION = "PHONE_CONSULTATION",
  VIDEO_CONSULTATION = "VIDEO_CONSULTATION",
  WRITTEN_CONSULTATION = "WRITTEN_CONSULTATION",
  LIVECHAT_CONSULTATION = "LIVECHAT_CONSULTATION",
  LONGEVITY = "LONGEVITY",
  NEWEST = "NEWEST",
  ALPHABETICAL = "ALPHABETICAL",
}

export interface ExpertFilters {
  consultationType: ConsultationType[];
  languages: Language[];
  skills: Skill[];
  hidden: boolean;
}

export interface ExpertFrontFilters {
  expertType: ExpertType[];
  search: string | null;
}
